import { useMemo } from "react"

import { useLocation, useMatch, useNavigate } from "react-router-dom"
import urlJoin from "url-join"

import { API_MODULE_DOCUMENT, APP_DOCUMENT, MODULE_DOCUMENTS } from "@l2r-front/l2r-app-doc"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { BottomSheetHeights, useApplicationDispatchContext } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../common/constants/i18n"
import { useGetActiveModules } from "../../hooks/useGetActiveModules"
import { cleanInnerPath } from "../../utils/urlUtils"

import * as Styled from "./MobileModulesSelector.styled"

export const MobileModulesSelector = (props) => {

    const {
        close,
    } = props

    const { t } = useTranslation()
    const { selectedNetwork } = useNetworksStateContext()
    const { storeMapBoundingBox } = useMapDispatchContext()
    const match = useMatch("/:selectedNetwork/:app/:module/*")
    const selectedModule = match?.params?.module
    const selectedApp = match?.params?.app
    const navigate = useNavigate()
    const location = useLocation()
    const appsWithActiveModules = useGetActiveModules()
    const { setCurrentBottomSheetHeight } = useApplicationDispatchContext()

    const modules = useMemo(() => {
        const sortedApps = Object.values(appsWithActiveModules).sort((a, b) => a.index - b.index).filter(app => app.path !== APP_DOCUMENT)
        const appsToDisplay = sortedApps.map(app => {
            const sortedModules = Object.values(app.modules).sort((a, b) => a.index - b.index)
            const appModules = sortedModules.map(module => {
                const modulePath = urlJoin("/", app.path ?? "", module.path)
                return ({
                    id: module.path,
                    label: t(app.i18nNamespace, module.label),
                    disabled: module.disabled,
                    value: modulePath,
                })
            })
            const apps = [
                app.label && <Styled.ListSubheader
                    key={t(app.i18nNamespace, app.label)}
                    variant="Regular"
                >
                    {app.icon({ color: "primary" })}
                    <Styled.AppTitle variant="Mobile/Body/Bold">
                        {t(app.i18nNamespace, app.label)}
                    </Styled.AppTitle>
                </Styled.ListSubheader>,
                ...appModules,
            ]

            return apps
        })
        if (selectedNetwork?.modules?.includes(API_MODULE_DOCUMENT)) {
            appsToDisplay.push(<Styled.ListSubheader
                key="general"
                variant="Regular"
            >
                < Styled.AppTitle variant="Mobile/Body/Bold" >
                    {t(I18N_NAMESPACE, "containers.moduleSelector.generalAppLabel")}
                </Styled.AppTitle >
            </Styled.ListSubheader >)
            appsToDisplay.push({
                id: API_MODULE_DOCUMENT,
                label: t("l2r-app-doc", "app.modules.documents"),
                value: `/${APP_DOCUMENT}/${MODULE_DOCUMENTS}`,
            })
        }

        return appsToDisplay
    }, [appsWithActiveModules, selectedNetwork?.modules, t])

    if (!modules?.length) {
        return <Styled.Text variant="Regular">
            {t(I18N_NAMESPACE, "containers.moduleSelector.emptyList")}
        </Styled.Text>
    }

    return <Styled.RadioButtonList
        id="modules-list-container"
        itemsList={modules?.flat()}
        onChange={(value) => {
            const previousPath = match?.params?.["*"].length ? match?.params?.["*"].replace(/code\/.*/, "") : ""
            const cleanedPreviousPath = cleanInnerPath(previousPath, match?.params?.app)
            storeMapBoundingBox()
            navigate(`/${selectedNetwork.slug}${value}/${cleanedPreviousPath}${location?.search}`)
            close()
            setCurrentBottomSheetHeight(BottomSheetHeights.SMALL)
        }}
        selectedValue={`/${selectedApp}/${selectedModule}`}
    />
}

MobileModulesSelector.propTypes = {
    close: PropTypes.func.isRequired,
}
